import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Globe, Sparkles, Loader2, ExternalLink, Check, RefreshCw, Trash2, Save,
  Plus, X as XIcon, Image as ImageIcon, Tag, Layers, Send, HelpCircle, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { tljAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { isTennis, TENNIS_CONFIG } from '../lib/tennis';

/**
 * TheLocalJewel product publisher (2026-07 API).
 *
 * Auto-fills the product page payload from existing product data:
 * title/slug/subtitle/description (AI), specifications table, FAQs — all
 * editable — then POSTs to thelocaljewel.com. Story content moved to the
 * Custom Pieces API (separate tab, coming later).
 */
export default function TLJListingPanel({ product, onUpdate }) {
  const { isEbayUser } = useAuth();  // Same allow-list as eBay
  const allowed = isEbayUser?.();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subtab, setSubtab] = useState('seo');

  // SEO editable state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [seoPhrases, setSeoPhrases] = useState([]);
  const [metaDescription, setMetaDescription] = useState('');
  const [newPhrase, setNewPhrase] = useState('');
  const [genSeo, setGenSeo] = useState(false);
  const [genPhrases, setGenPhrases] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);

  // Specs editable state
  const [specTable, setSpecTable] = useState({});
  const [certNumber, setCertNumber] = useState('');
  const [certLink, setCertLink] = useState('');
  const [savingSpecs, setSavingSpecs] = useState(false);

  // FAQs editable state
  const [faqs, setFaqs] = useState([]);
  const [genFaqs, setGenFaqs] = useState(false);
  const [savingFaqs, setSavingFaqs] = useState(false);

  const [featured, setFeatured] = useState(false);
  const [collections, setCollections] = useState([]);
  const [newCollection, setNewCollection] = useState('');
  const [savingCollections, setSavingCollections] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [priceMatrix, setPriceMatrix] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [pushingPrice, setPushingPrice] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!allowed) return;
    try {
      setLoading(true);
      const res = await tljAPI.preview(product.id);
      setPreview(res.data);
      const p = res.data.payload || {};
      const x = res.data.extras || {};
      setTitle(p.title || '');
      setSlug(p.slug || '');
      setSubtitle(p.subtitle || '');
      setDescription(x.description || p.description || '');
      setSeoPhrases(Array.isArray(x.seo_phrases) ? x.seo_phrases : (p.seo_phrases || []));
      setMetaDescription(x.meta_description || '');
      // Spec table: initialize every schema row (merged payload value or blank)
      const merged = p.spec_table || {};
      const rows = {};
      (res.data.spec_schema || []).forEach(({ key }) => { rows[key] = merged[key] || ''; });
      setSpecTable(rows);
      setCertNumber(x.cert_number || '');
      setCertLink(x.cert_link || '');
      setFaqs(Array.isArray(p.faqs) ? p.faqs : []);
      setFeatured(!!x.featured);
      setCollections(Array.isArray(p.collections) ? p.collections : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load TLJ preview');
    } finally {
      setLoading(false);
    }
  }, [allowed, product.id]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleGenerateSEO = async () => {
    try {
      setGenSeo(true);
      const res = await tljAPI.generateSeo(product.id, {});
      setTitle(res.data.title || '');
      setSlug(res.data.slug || '');
      setSubtitle(res.data.subtitle || '');
      if (res.data.description) setDescription(res.data.description);
      toast.success('SEO content generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SEO generation failed');
    } finally {
      setGenSeo(false);
    }
  };

  const handleSaveSEO = async () => {
    try {
      setSavingSeo(true);
      await tljAPI.editSeo(product.id, { title, slug, subtitle, description, seo_phrases: seoPhrases, meta_description: metaDescription });
      toast.success('SEO fields saved');
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSeo(false);
    }
  };

  const handleGenerateSeoPhrases = async () => {
    try {
      setGenPhrases(true);
      const res = await tljAPI.generateSeoPhrases(product.id);
      setSeoPhrases(res.data.seo_phrases || []);
      if (res.data.meta_description) setMetaDescription(res.data.meta_description);
      toast.success(`${(res.data.seo_phrases || []).length} SEO phrases generated — review then Save`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SEO phrase generation failed');
    } finally {
      setGenPhrases(false);
    }
  };

  const handleAddPhrase = () => {
    const s = newPhrase.trim().toLowerCase();
    if (!s) return;
    if (seoPhrases.includes(s)) { toast.error('Phrase already in the list'); return; }
    if (seoPhrases.length >= 20) { toast.error('Max 20 phrases'); return; }
    setSeoPhrases((p) => [...p, s]);
    setNewPhrase('');
  };

  const handleSaveSpecs = async () => {
    try {
      setSavingSpecs(true);
      await tljAPI.editExtras(product.id, { spec_table: specTable, cert_number: certNumber, cert_link: certLink });
      toast.success('Specifications saved');
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save specs failed');
    } finally {
      setSavingSpecs(false);
    }
  };

  const handleAddCollection = () => {
    const c = newCollection.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
    if (!c) return;
    if (collections.includes(c)) { toast.error('Collection already in the list'); return; }
    if (collections.length >= 6) { toast.error('Max 6 collections'); return; }
    setCollections((p) => [...p, c]);
    setNewCollection('');
  };

  const handleSaveCollections = async () => {
    try {
      setSavingCollections(true);
      await tljAPI.editExtras(product.id, { collections });
      toast.success('Collections saved');
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save collections failed');
    } finally {
      setSavingCollections(false);
    }
  };

  const handleResetCollections = () => {
    setCollections(preview?.auto_collections || []);
    toast.info('Reset to auto-derived collections — hit Save to persist');
  };

  const handleResetSpecs = () => {
    const auto = preview?.auto_spec_table || {};
    const rows = {};
    (preview?.spec_schema || []).forEach(({ key }) => { rows[key] = auto[key] || ''; });
    setSpecTable(rows);
    toast.info('Reset to auto-filled values — hit Save to persist');
  };

  const handleSaveFaqs = async () => {
    try {
      setSavingFaqs(true);
      await tljAPI.editExtras(product.id, { faqs });
      toast.success('FAQs saved');
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save FAQs failed');
    } finally {
      setSavingFaqs(false);
    }
  };

  const handleGenerateFaqs = async () => {
    try {
      setGenFaqs(true);
      const res = await tljAPI.generateFaqs(product.id);
      setFaqs(res.data.faqs || []);
      toast.success(`${(res.data.faqs || []).length} FAQs generated — review then Save`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'FAQ generation failed');
    } finally {
      setGenFaqs(false);
    }
  };

  const handleResetFaqs = () => {
    setFaqs(preview?.default_faqs || []);
    toast.info('Reset to auto-filled FAQs — hit Save to persist');
  };

  const handleSaveFeatured = async (value) => {
    setFeatured(value);
    try {
      await tljAPI.editExtras(product.id, { featured: value });
    } catch (err) {
      toast.error('Failed to save featured flag');
    }
  };

  const pollPublishStatus = useCallback(async () => {
    for (let i = 0; i < 75; i++) {           // ~5 minutes max
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const res = await tljAPI.publishStatus(product.id);
        const job = res.data.job || {};
        if (job.status === 'success') {
          if (job.action === 'created') {
            toast.success('Draft created on thelocaljewel.com. Make it live from TLJ Admin.');
          } else {
            toast.success('Project updated on thelocaljewel.com (live/draft status unchanged)');
          }
          if (job.public_url && job.visibility !== 'draft') window.open(job.public_url, '_blank', 'noopener,noreferrer');
          setPublishing(false);
          await loadPreview();
          return;
        }
        if (job.status === 'error') {
          toast.error(job.error || 'Publish failed');
          setPublishing(false);
          await loadPreview();
          return;
        }
      } catch (e) { /* transient poll error — keep polling */ }
    }
    setPublishing(false);
    toast.error('Publish is taking unusually long — reload the page to check status.');
  }, [product.id, loadPreview]);

  // Resume polling if a publish was already running (e.g. page reload mid-publish)
  useEffect(() => {
    if (preview?.publish_job?.status === 'running' && !publishing) {
      setPublishing(true);
      pollPublishStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const handlePublish = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!preview?.hero_url) { toast.error('At least one photo is required'); return; }
    const msg = isPublished
      ? 'Push these updates to thelocaljewel.com? The live/draft status on the website stays unchanged.'
      : 'Push this product to thelocaljewel.com as a DRAFT? It stays hidden until your team publishes it from TLJ Admin.';
    if (!window.confirm(msg)) return;
    try {
      setPublishing(true);
      await tljAPI.publish(product.id);
      toast.info('Pushing in background — usually takes 30–90 seconds…');
      pollPublishStatus();
    } catch (err) {
      setPublishing(false);
      toast.error(err.response?.data?.detail || 'Publish failed');
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Remove this product from thelocaljewel.com? It will no longer be visible to visitors.')) return;
    try {
      setUnpublishing(true);
      await tljAPI.unpublish(product.id);
      toast.success('Project removed');
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unpublish failed');
    } finally {
      setUnpublishing(false);
    }
  };

  const loadPriceMatrix = useCallback(async () => {
    try {
      setLoadingPrice(true);
      const res = await tljAPI.priceMatrix(product.id);
      setPriceMatrix(res.data);
    } catch (err) {
      setPriceMatrix(null);
    } finally {
      setLoadingPrice(false);
    }
  }, [product.id]);

  useEffect(() => {
    if (subtab === 'publish') {
      loadPriceMatrix();
    }
  }, [subtab, loadPriceMatrix]);

  const handlePushPricingOnly = async () => {
    if (!window.confirm('Push the latest pricing matrix to thelocaljewel.com for this listing? (No other fields will change.)')) return;
    try {
      setPushingPrice(true);
      const res = await tljAPI.pushPricing(product.id);
      toast.success(`Pricing pushed (From $${(res.data.from_price || 0).toLocaleString()})`);
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push pricing failed');
    } finally {
      setPushingPrice(false);
    }
  };

  if (!allowed) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-5 text-sm text-zinc-500">
        TLJ publishing is restricted to authorized users.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
        <Loader2 className="animate-spin mx-auto text-zinc-400" />
      </div>
    );
  }

  const specs = preview?.payload?.specs || {};
  const tags = preview?.payload?.tags || [];
  const isPublished = preview?.tlj_status === 'published';
  const isDraft = isPublished && preview?.tlj_visibility === 'draft';
  const specSchema = preview?.spec_schema || [];
  const settingStoneKeys = ['setting_stone_carat', 'setting_stone_shape', 'setting_stone_color', 'setting_stone_clarity'];

  return (
    <div className="space-y-4" data-testid="tlj-listing-panel">
      {/* Header / status card */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <Globe size={15} className="text-emerald-600" /> Publish to TheLocalJewel.com
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Push this product to your website as a <strong>draft</strong>. Your team makes it live from TLJ Admin. Title, description, specifications and FAQs auto-fill, so edit anything before pushing.
            </p>
          </div>
          {isDraft ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium" data-testid="tlj-status-pill">
              <Check size={11} /> Draft on TLJ · publish from TLJ Admin
            </span>
          ) : isPublished ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium" data-testid="tlj-status-pill">
              <Check size={11} /> Live on thelocaljewel.com
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-medium">
              Not published
            </span>
          )}
        </div>

        {isPublished && preview?.tlj_published_url && (
          <div className="bg-emerald-50/40 border border-emerald-100 rounded-md p-3 space-y-2">
            {isDraft && (
              <div className="text-[11px] text-amber-800" data-testid="tlj-draft-note">
                Hidden from visitors until published in TLJ Admin. Once live, it will be at:
              </div>
            )}
            <a
              href={preview.tlj_published_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-emerald-700 hover:text-emerald-900 font-medium break-all"
              data-testid="tlj-live-link"
            >
              <ExternalLink size={11} /> {preview.tlj_published_url}
            </a>
            <div className="text-[11px] text-emerald-900/70">
              {isDraft ? 'Pushed as draft' : 'Published'} {preview.tlj_published_at && new Date(preview.tlj_published_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Sub-tab nav */}
      <div className="flex flex-wrap border-b border-zinc-200 bg-zinc-50/60 rounded-t-lg overflow-hidden" data-testid="tlj-subtab-nav">
        {[
          { key: 'seo',     label: 'Title · SEO',    icon: Tag },
          { key: 'specs',   label: 'Specifications', icon: Layers },
          { key: 'faqs',    label: 'FAQs',           icon: HelpCircle },
          { key: 'publish', label: 'Publish',        icon: Send },
        ].map((t) => {
          const Icon = t.icon;
          const active = subtab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setSubtab(t.key)}
              className={`flex-1 min-w-[130px] inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium border-r border-zinc-200 last:border-r-0 transition-colors ${
                active ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:bg-white hover:text-zinc-800'
              }`}
              data-testid={`tlj-subtab-${t.key}`}
            >
              <Icon size={12} className={active ? 'text-emerald-600' : ''} />
              {t.label}
            </button>
          );
        })}
      </div>

      {subtab === 'seo' && (
        <>
      {/* SEO section */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="tlj-seo-section">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Sparkles size={13} className="text-violet-600" /> Title · Slug · Subtitle · Description
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={handleGenerateSEO} disabled={genSeo}
              className="text-[11px]" data-testid="tlj-generate-seo-btn"
            >
              {genSeo ? <><Loader2 size={11} className="mr-1 animate-spin" /> Generating…</>
                     : <><Sparkles size={11} className="mr-1" /> Generate with AI</>}
            </Button>
            <Button
              variant="outline" size="sm" onClick={handleSaveSEO} disabled={savingSeo}
              className="text-[11px]" data-testid="tlj-save-seo-btn"
            >
              {savingSeo ? <Loader2 size={11} className="animate-spin" /> : <><Save size={11} className="mr-1" /> Save</>}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Field label={`Title (${title.length}/120 ideal — Etsy-style, keywords first)`} value={title} onChange={setTitle} placeholder="Oval Lab Grown Diamond Engagement Ring – Hidden Halo 14K White Gold – IGI Certified" testId="tlj-title" />
          <Field label="Slug" value={slug} onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9-]+/g, '-'))} placeholder="oval-lab-diamond-hidden-halo-engagement-ring" testId="tlj-slug" />
          <Field label={`Subtitle (${subtitle.length}/150 ideal)`} value={subtitle} onChange={setSubtitle} placeholder="A short benefit-driven caption..." testId="tlj-subtitle" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Product description <span className="text-zinc-400 normal-case">(2–4 sentences — shows in the buy panel, first sentence matters most)</span>
              </div>
              <span className="text-[10px] text-zinc-400">{description.length} chars</span>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short product paragraph about the piece — materials, design, what makes it special. No story tone."
              rows={4}
              className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="tlj-description"
            />
          </div>

          {/* SEO phrases (Fable 5) */}
          <div className="pt-3 border-t border-zinc-100 space-y-2" data-testid="tlj-seo-phrases-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                SEO phrases <span className="text-zinc-400 normal-case">({seoPhrases.length}/20 — pushed as search keywords on the product page)</span>
              </div>
              <Button
                variant="outline" size="sm" onClick={handleGenerateSeoPhrases} disabled={genPhrases}
                className="text-[11px]" data-testid="tlj-generate-seo-phrases-btn"
              >
                {genPhrases ? <><Loader2 size={11} className="mr-1 animate-spin" /> Generating…</>
                            : <><Sparkles size={11} className="mr-1" /> Generate (Fable 5)</>}
              </Button>
            </div>
            {seoPhrases.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {seoPhrases.map((s, i) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-100 text-violet-800 text-[11px]" data-testid={`tlj-seo-phrase-${i}`}>
                    {s}
                    <button onClick={() => setSeoPhrases((p) => p.filter((_, j) => j !== i))} className="text-violet-400 hover:text-red-600" data-testid={`tlj-seo-phrase-delete-${i}`}>
                      <XIcon size={10} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-zinc-400 italic">No phrases yet — click Generate (Fable 5) or add your own below.</div>
            )}
            <div className="flex items-center gap-2">
              <Input
                value={newPhrase}
                onChange={(e) => setNewPhrase(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhrase(); } }}
                placeholder="add a phrase and press Enter..."
                className="text-[12px] h-8 max-w-xs"
                data-testid="tlj-seo-phrase-input"
              />
              <Button variant="ghost" size="sm" onClick={handleAddPhrase} className="text-[11px]" data-testid="tlj-seo-phrase-add-btn">
                <Plus size={11} className="mr-1" /> Add
              </Button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                Meta description <span className="text-zinc-400 normal-case">({metaDescription.length}/160 — Google snippet; falls back to subtitle if empty)</span>
              </div>
              <Input
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Auto-generated with the phrases — edit if needed"
                className="text-[12px]"
                data-testid="tlj-meta-description"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Auto-filled specs / tags preview */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="tlj-specs-preview">
        <h3 className="text-sm font-semibold text-zinc-900">Auto-filled from product</h3>
        {Object.keys(specs).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
            {Object.entries(specs).map(([k, v]) => (
              <div key={k} className="bg-zinc-50 rounded-md px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, ' ')}</div>
                <div className="text-zinc-900 font-medium">{String(v)}</div>
              </div>
            ))}
          </div>
        )}
        {tags.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[11px]">{t}</span>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-500 pt-2 border-t border-zinc-100">
          <div>Photos detected: <span className="font-medium text-zinc-700">{preview?.photo_count || 0}</span></div>
          <div>Product type: <span className="font-medium text-zinc-700">{preview?.product_type?.replace(/_/g, ' ') || '—'}</span></div>
        </div>
      </div>
        </>
      )}

      {subtab === 'specs' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="tlj-spec-table-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
                <Layers size={13} className="text-emerald-600" /> Specifications table
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Auto-filled from product data — rendered as the Specifications table on the product page. Blank rows are hidden{specSchema.some((r) => settingStoneKeys.includes(r.key)) ? ' (setting-stone rows show "N/A")' : ''}.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleResetSpecs} className="text-[11px] text-zinc-500" data-testid="tlj-reset-specs-btn">
                <RotateCcw size={11} className="mr-1" /> Reset to auto
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveSpecs} disabled={savingSpecs} className="text-[11px]" data-testid="tlj-save-specs-btn">
                {savingSpecs ? <Loader2 size={11} className="animate-spin" /> : <><Save size={11} className="mr-1" /> Save</>}
              </Button>
            </div>
          </div>

          {specSchema.length === 0 ? (
            <div className="text-[12px] text-zinc-400 italic">
              This product type ({preview?.product_type?.replace(/_/g, ' ')}) doesn't have a spec table on thelocaljewel.com yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {specSchema.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <div className="w-40 flex-shrink-0 text-[11px] text-zinc-500">
                    {label}
                    {settingStoneKeys.includes(key) && <span className="text-zinc-300"> *</span>}
                  </div>
                  <Input
                    value={specTable[key] || ''}
                    onChange={(e) => setSpecTable((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="text-[13px] h-8"
                    data-testid={`tlj-spec-${key}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Cert */}
          <div className="pt-3 border-t border-zinc-100">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Certificate</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Field label="IGI cert number" value={certNumber} onChange={setCertNumber} placeholder="LG687583822" testId="tlj-cert-number" />
              <Field label="IGI cert link (optional)" value={certLink} onChange={setCertLink} placeholder="https://www.igi.org/reports/..." testId="tlj-cert-link" />
            </div>
          </div>
        </div>
      )}

      {subtab === 'faqs' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="tlj-faqs-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
                <HelpCircle size={13} className="text-sky-600" /> Product FAQs <span className="text-zinc-400 font-normal">({faqs.length}/20)</span>
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Shown on the product page + emitted as Google FAQ schema. Auto-filled — edit, add your own, or regenerate with AI.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="ghost" size="sm" onClick={handleResetFaqs} className="text-[11px] text-zinc-500" data-testid="tlj-reset-faqs-btn">
                <RotateCcw size={11} className="mr-1" /> Reset to auto
              </Button>
              <Button variant="outline" size="sm" onClick={handleGenerateFaqs} disabled={genFaqs} className="text-[11px]" data-testid="tlj-generate-faqs-btn">
                {genFaqs ? <><Loader2 size={11} className="mr-1 animate-spin" /> Generating…</>
                         : <><Sparkles size={11} className="mr-1" /> Generate with AI</>}
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveFaqs} disabled={savingFaqs} className="text-[11px]" data-testid="tlj-save-faqs-btn">
                {savingFaqs ? <Loader2 size={11} className="animate-spin" /> : <><Save size={11} className="mr-1" /> Save</>}
              </Button>
            </div>
          </div>

          {faqs.length === 0 ? (
            <div className="text-[12px] text-zinc-400 italic">No FAQs — the product page will show the store-wide default set.</div>
          ) : (
            <div className="space-y-2">
              {faqs.map((f, i) => (
                <div key={i} className="bg-zinc-50 rounded-md p-2.5 space-y-1.5" data-testid={`tlj-faq-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">Q{i + 1}</span>
                    <Input
                      value={f.question}
                      onChange={(e) => setFaqs((prev) => prev.map((x, j) => j === i ? { ...x, question: e.target.value } : x))}
                      placeholder="Question..."
                      className="text-sm h-8 flex-1 font-medium"
                      data-testid={`tlj-faq-question-${i}`}
                    />
                    <button onClick={() => setFaqs((p) => p.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600 flex-shrink-0" data-testid={`tlj-faq-delete-${i}`}>
                      <XIcon size={13} />
                    </button>
                  </div>
                  <textarea
                    value={f.answer}
                    onChange={(e) => setFaqs((prev) => prev.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))}
                    placeholder="Answer..."
                    rows={2}
                    className="w-full text-[12px] border border-zinc-200 rounded px-2 py-1.5"
                    data-testid={`tlj-faq-answer-${i}`}
                  />
                </div>
              ))}
            </div>
          )}

          {faqs.length < 20 && (
            <Button
              variant="ghost" size="sm"
              onClick={() => setFaqs((p) => [...p, { question: '', answer: '' }])}
              className="text-[11px]" data-testid="tlj-add-faq-btn"
            >
              <Plus size={11} className="mr-1" /> Add FAQ
            </Button>
          )}
        </div>
      )}

      {subtab === 'publish' && (
        <>
      {/* Hero preview */}
      {preview?.hero_url && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-2" data-testid="tlj-hero-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
              <ImageIcon size={13} className="text-rose-600" /> Hero photo
            </h3>
            <span className="text-[11px] text-zinc-400">+ {Math.max(0, (preview.photo_count || 1) - 1)} gallery photos</span>
          </div>
          <img src={preview.hero_url} alt="Hero" className="w-full max-h-72 object-cover rounded-md bg-zinc-100" />
        </div>
      )}

      {/* TLJ price matrix */}
      {priceMatrix?.supported && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="tlj-price-matrix">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
                <Tag size={13} className="text-emerald-600" /> TLJ price matrix
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                All-in USD, <strong>no platform fees</strong>. Auto-pushed on Publish.
                {priceMatrix.from_price != null && (
                  <> · <span className="text-emerald-700 font-semibold">From ${priceMatrix.from_price.toLocaleString()}</span></>
                )}
              </p>
            </div>
            {isPublished && (
              <Button
                size="sm"
                variant="outline"
                onClick={handlePushPricingOnly}
                disabled={pushingPrice}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="tlj-push-pricing-btn"
              >
                {pushingPrice
                  ? <><Loader2 size={11} className="mr-1.5 animate-spin" /> Pushing…</>
                  : <><Send size={11} className="mr-1.5" /> Push pricing only</>}
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            {priceMatrix.shape === 'metal_only' ? (
              <table className="w-full text-xs border-collapse" data-testid="tlj-price-matrix-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-2 py-1.5 font-medium text-zinc-600">Metal</th>
                    <th className="text-right px-2 py-1.5 font-medium text-zinc-600">All-in price</th>
                  </tr>
                </thead>
                <tbody>
                  {(priceMatrix.tiers || []).map((tier) => {
                    const price = priceMatrix.matrix?.[tier];
                    return (
                      <tr key={tier} className="border-b border-zinc-100 last:border-0">
                        <td className="px-2 py-1.5 font-medium text-zinc-700 capitalize">
                          {tier.replace('k', 'K')}
                        </td>
                        <td className="text-right px-2 py-1.5 font-mono text-zinc-900">
                          {price != null ? `$${price.toLocaleString()}` : <span className="text-zinc-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-xs border-collapse" data-testid="tlj-price-matrix-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-2 py-1.5 font-medium text-zinc-600">Metal</th>
                    {(priceMatrix.carats || []).map((c) => (
                      <th key={c} className="text-right px-2 py-1.5 font-medium text-zinc-600">
                        {c} CT
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(priceMatrix.tiers || []).map((tier) => {
                    const row = priceMatrix.matrix?.[tier] || {};
                    return (
                      <tr key={tier} className="border-b border-zinc-100 last:border-0">
                        <td className="px-2 py-1.5 font-medium text-zinc-700 capitalize">
                          {tier.replace('k', 'K')}
                        </td>
                        {(priceMatrix.carats || []).map((c) => (
                          <td key={c} className="text-right px-2 py-1.5 font-mono text-zinc-900">
                            {row[c] != null ? `$${row[c].toLocaleString()}` : <span className="text-zinc-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {isTennis(priceMatrix.product_type) && (
            <p className="text-[11px] text-emerald-800 bg-emerald-50/60 border border-emerald-100 rounded-md px-2.5 py-1.5" data-testid="tlj-tennis-price-note">
              💎 {TENNIS_CONFIG[priceMatrix.product_type].label}: 14K White / Yellow only · {TENNIS_CONFIG[priceMatrix.product_type].tljNote}
            </p>
          )}
          {!isPublished && (
            <p className="text-[11px] text-zinc-400 italic">
              The matrix is sent automatically the first time you publish. Use the standalone push button only after publishing.
            </p>
          )}
        </div>
      )}
      {priceMatrix && !priceMatrix.supported && priceMatrix.message && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800" data-testid="tlj-price-matrix-unavailable">
          {priceMatrix.message}
        </div>
      )}
      {loadingPrice && !priceMatrix && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 text-xs text-zinc-400 inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading price matrix…
        </div>
      )}

      {/* Storefront collections — buyability */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="tlj-collections-card">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Tag size={13} className="text-violet-600" /> Storefront collections
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Required for the product to be <strong>buyable</strong> — with no collections the page is lead-gen only (no buy panel, no pricing table).
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {collections.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2.5 py-0.5 text-[11px]" data-testid={`tlj-collection-chip-${c}`}>
              {c}
              <button onClick={() => setCollections((p) => p.filter((x) => x !== c))} className="text-violet-400 hover:text-violet-700" data-testid={`tlj-collection-remove-${c}`}>
                <XIcon size={11} />
              </button>
            </span>
          ))}
          {collections.length === 0 && (
            <span className="text-[11px] text-amber-700 bg-amber-50/40 border border-amber-100 rounded-md px-2 py-1" data-testid="tlj-collections-empty-warning">
              ⚠ No collections — this product will NOT be buyable on the storefront.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={newCollection}
            onChange={(e) => setNewCollection(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCollection(); } }}
            placeholder={isTennis(preview?.product_type) ? `e.g. tennis-${TENNIS_CONFIG[preview.product_type].noun}s` : 'e.g. oval-engagement-rings'}
            className="text-xs h-8 max-w-xs"
            data-testid="tlj-collection-input"
          />
          <Button size="sm" variant="outline" onClick={handleAddCollection} className="h-8" data-testid="tlj-collection-add-btn">
            <Plus size={11} className="mr-1" /> Add
          </Button>
          <Button size="sm" onClick={handleSaveCollections} disabled={savingCollections} className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="tlj-collections-save-btn">
            {savingCollections ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Save size={11} className="mr-1" />} Save
          </Button>
          <Button size="sm" variant="ghost" onClick={handleResetCollections} className="h-8 text-zinc-500" data-testid="tlj-collections-reset-btn">
            <RotateCcw size={11} className="mr-1" /> Reset to auto
          </Button>
        </div>
      </div>

      {/* Publish actions */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3">
        <label className="flex items-center gap-2 text-[12px] text-zinc-700 cursor-pointer">
          <input type="checkbox" checked={featured} onChange={(e) => handleSaveFeatured(e.target.checked)} data-testid="tlj-featured-toggle" />
          <span>Mark as featured (homepage hero)</span>
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handlePublish}
            disabled={publishing || !title.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="tlj-publish-btn"
          >
            {publishing ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Pushing…</>
                       : isPublished ? <><RefreshCw size={13} className="mr-1.5" /> Update on TheLocalJewel.com</>
                                     : <><Globe size={13} className="mr-1.5" /> Push as Draft to TheLocalJewel.com</>}
          </Button>
          {isPublished && (
            <Button
              variant="outline" onClick={handleUnpublish} disabled={unpublishing}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="tlj-unpublish-btn"
            >
              {unpublishing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Trash2 size={13} className="mr-1.5" />}
              Unpublish
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={loadPreview} className="ml-auto text-zinc-500" data-testid="tlj-reload-btn">
            <RefreshCw size={11} className="mr-1" /> Reload preview
          </Button>
        </div>
        {!title.trim() && (
          <div className="text-[11px] text-amber-700 bg-amber-50/40 border border-amber-100 rounded-md p-2">
            Set a title in the <strong>Title · SEO</strong> tab (or click <strong>Generate with AI</strong>) before publishing.
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-sm"
        data-testid={testId}
      />
    </div>
  );
}
